/**
 * IXVerify v2 — every page of a report, rendered in place (2026-10-03).
 *
 * The report is one PDF: the summary page(s), then THE CERTIFIED COPY — every
 * IXRS assignment page, every kit label photo — then the signatures on the
 * summary. The owner's rule: wherever a report is shown, ALL of it is shown.
 * An `<iframe>` embed could not keep that promise (mobile browsers draw only
 * the first page of an embedded PDF), so the pages are rendered here with
 * pdf.js (`react-pdf`, the platform's existing viewer dependency) as a
 * vertical stack sized to the container — the same on a phone and a laptop.
 *
 * The bytes arrive as a Blob from `getReportBlob` (served by the api from its
 * own origin; a bucket URL is cross-origin to pdf.js). Text and annotation
 * layers are off: the report is read, not selected, and the full-resolution
 * evidence pages are heavy enough already.
 *
 * R3: the report carries the DOB. The Blob lives in the caller's state and
 * never reaches a URL, a log line, or an error sink.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { FileWarning, Loader2 } from "lucide-react";

// The worker is served by the same CDN the MHR, Intelligence and CRM viewers
// already use (one pattern, one CSP entry).
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function ReportPages({
  file,
  onPageCount,
  onError,
}: {
  file: Blob;
  /** Fired once the document opens, with its true page count. */
  onPageCount?: (numPages: number) => void;
  /** Fired when pdf.js cannot open the bytes. */
  onError?: (err: Error) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const [numPages, setNumPages] = useState(0);
  const [failed, setFailed] = useState(false);

  // Pages follow the container's width (a phone gets the whole width, a
  // laptop a readable column); re-measured on resize and orientation change.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    if (el.clientWidth > 0) setWidth(Math.floor(el.clientWidth));
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.floor(entry.contentRect.width);
        if (w > 0) setWidth(w);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // No reset-on-file effect here: a child's effects run before the parent's,
  // so a reset keyed on `file` would wipe a synchronously reported page count.
  // The caller remounts this component per report; a load failure clears when
  // the next load succeeds.
  const handleLoad = useCallback(
    ({ numPages: n }: { numPages: number }) => {
      setFailed(false);
      setNumPages(n);
      onPageCount?.(n);
    },
    [onPageCount],
  );

  const handleError = useCallback(
    (err: Error) => {
      setFailed(true);
      onError?.(err);
    },
    [onError],
  );

  return (
    <div
      ref={containerRef}
      className="w-full"
      data-testid="ixv-report-pages"
      data-page-count={numPages || ""}
    >
      {failed ? (
        <div
          className="flex flex-col items-center justify-center gap-2 p-6 text-center"
          role="alert"
        >
          <FileWarning
            className="h-8 w-8 text-gray-400 dark:text-gray-500"
            aria-hidden="true"
          />
          <p className="text-sm text-gray-600 dark:text-gray-300">
            This report could not be displayed here. Use Open in new tab or
            Download.
          </p>
        </div>
      ) : (
        <Document
          file={file}
          onLoadSuccess={handleLoad}
          onLoadError={handleError}
          loading={
            <div className="flex items-center justify-center py-16">
              <Loader2
                className="h-6 w-6 animate-spin text-primary-600 motion-reduce:animate-none dark:text-primary-400"
                aria-hidden="true"
              />
            </div>
          }
          error={null}
        >
          {Array.from({ length: numPages }, (_, i) => i + 1).map(
            (pageNumber) => (
              <figure
                key={pageNumber}
                className="mb-4 last:mb-0"
                data-testid="ixv-report-page"
              >
                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700">
                  <Page
                    pageNumber={pageNumber}
                    width={width}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    loading={
                      <div className="flex items-center justify-center py-16">
                        <Loader2
                          className="h-5 w-5 animate-spin text-primary-600 motion-reduce:animate-none dark:text-primary-400"
                          aria-hidden="true"
                        />
                      </div>
                    }
                  />
                </div>
                <figcaption className="mt-1 text-center text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500">
                  Page {pageNumber} of {numPages}
                </figcaption>
              </figure>
            ),
          )}
        </Document>
      )}
    </div>
  );
}
